// DATEV/StB config the Exports page needs before export-datev can build an
// AGENDA bundle. The config lives in `tenant_settings` as namespaced
// `datev.*` key/value rows (see migration 20260517220000). This module is
// the frontend-side mirror of the backend preflight in
// export-batch-builder.ts: the same required-non-blank + positive-integer +
// DATEV v060 range rules, so the Settings tab can reject a bad config before
// the round-trip rather than after a 412 from the Edge Function.

export type DatevSettingKind = 'positive-integer' | 'text' | 'iso-date';

export interface DatevSettingField {
  readonly key: string;
  readonly label: string;
  readonly help: string;
  readonly required: boolean;
  readonly kind: DatevSettingKind;
  readonly placeholder: string;
  // Inclusive bounds for positive-integer fields, matching the DATEV v060
  // schema facets (de:p10049 / de:p10050). Undefined for text fields.
  readonly min?: number;
  readonly max?: number;
}

// Field order is the on-screen order; keep the two DATEV-header numbers
// (Berater/Mandant) first, then the AGENDA posting circle, then the
// optional generator label.
export const DATEV_SETTING_FIELDS: readonly DatevSettingField[] = [
  {
    key: 'datev.berater_number',
    label: 'Beraternummer',
    help: "Your tax advisor's DATEV consultant number (Berater-Nr.). Whole number from 1000 to 9999999.",
    required: true,
    kind: 'positive-integer',
    min: 1000,
    max: 9999999,
    placeholder: 'e.g. 12345',
  },
  {
    key: 'datev.mandant_number',
    label: 'Mandantennummer',
    help: 'Your DATEV client number (Mandanten-Nr.) at the tax advisor. Whole number from 1 to 99999.',
    required: true,
    kind: 'positive-integer',
    min: 1,
    max: 99999,
    placeholder: 'e.g. 54321',
  },
  {
    key: 'datev.buchungskreis_eingang',
    label: 'Buchungskreis (Eingangsrechnungen)',
    help: 'Posting circle AGENDA files incoming invoices under.',
    required: true,
    kind: 'text',
    placeholder: 'Eingangsrechnungen',
  },
  {
    key: 'datev.generator_info',
    label: 'Generator-Info',
    help: "Free-text label embedded in the DATEV header. Defaults to 'Observatory' when blank.",
    required: false,
    kind: 'text',
    placeholder: 'Observatory',
  },
  // EXTF Buchungsstapel (Phase 2) config. All optional — the export builder
  // applies sensible defaults (SKR03 / Sachkontenlänge 4 / WJ = the exported
  // period's year) when a key is blank, so an existing tenant keeps exporting
  // without re-saving.
  {
    key: 'datev.skr_variant',
    label: 'Kontenrahmen (SKR)',
    help: "DATEV chart of accounts. Defaults to 'SKR03' when blank.",
    required: false,
    kind: 'text',
    placeholder: 'SKR03',
  },
  {
    key: 'datev.sachkontenlaenge',
    label: 'Sachkontenlänge',
    // Pinned at 4: the rest of the pipeline hard-codes 4-digit Sachkonten and
    // 5-digit Personenkonten (counterparties.creditor_number CHECK ~ '^[0-9]{5}$',
    // SACHKONTO_RE = ^[0-9]{4,5}$). A longer Sachkontenlänge in the header would
    // not match those account numbers, so DATEV would reject the stack.
    help: 'Digits per Sachkonto (Personenkonten are one longer). Currently fixed at 4 (SKR03).',
    required: false,
    kind: 'positive-integer',
    min: 4,
    max: 4,
    placeholder: '4',
  },
  {
    key: 'datev.wirtschaftsjahr_beginn',
    label: 'Wirtschaftsjahr-Beginn',
    help: "First day of the fiscal year (YYYY-MM-DD). Defaults to the exported period's year (Jan 1) when blank.",
    required: false,
    kind: 'iso-date',
    placeholder: '2026-01-01',
  },
  {
    key: 'datev.export_bezeichnung',
    label: 'Stapel-Bezeichnung',
    help: 'Free-text label for the Buchungsstapel (header field 17). Optional.',
    required: false,
    kind: 'text',
    placeholder: 'Observatory EXTF Buchungsstapel',
  },
];

const DATEV_SETTING_KEYS: ReadonlySet<string> = new Set(DATEV_SETTING_FIELDS.map((f) => f.key));

// Reduce raw `tenant_settings` rows to a values map keyed by setting key,
// keeping only the DATEV fields this tab owns. The table is a shared
// key/value bag, so a `billing.*` or future namespace must not leak into
// the form.
export function parseDatevSettings(
  rows: ReadonlyArray<{ key: string; value: string }>,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const row of rows) {
    if (DATEV_SETTING_KEYS.has(row.key)) values[row.key] = row.value;
  }
  return values;
}

// Turn a form values map into the rows to upsert into `tenant_settings`.
// Values are trimmed and blanks dropped: a required field is guaranteed
// non-blank by validateDatevSettings, and clearing the optional
// generator should remove the row so the backend default re-applies
// rather than persisting an empty string the builder would honour.
export function buildDatevSettingsUpsert(
  values: Readonly<Record<string, string | undefined>>,
): Array<{ key: string; value: string }> {
  const rows: Array<{ key: string; value: string }> = [];
  for (const field of DATEV_SETTING_FIELDS) {
    const trimmed = (values[field.key] ?? '').trim();
    if (trimmed.length > 0) rows.push({ key: field.key, value: trimmed });
  }
  return rows;
}

export interface DatevSettingError {
  readonly key: string;
  readonly reason: 'missing' | 'invalid';
  readonly message: string;
}

// Mirror of export-batch-builder.ts#isPositiveIntegerString: a run of
// ASCII digits that parses to an integer > 0. Leading zeros are fine;
// signs, decimals, and whitespace-in-the-middle are not.
function isPositiveIntegerString(trimmed: string): boolean {
  if (!/^\d+$/.test(trimmed)) return false;
  const n = Number(trimmed);
  return Number.isInteger(n) && n > 0;
}

// A YYYY-MM-DD string that is also a real calendar date (so 2026-13-01 and
// 2026-02-30 are rejected, not just non-ISO shapes like 01.01.2026).
function isRealIsoDate(trimmed: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;
  const y = Number(trimmed.slice(0, 4));
  const m = Number(trimmed.slice(5, 7));
  const d = Number(trimmed.slice(8, 10));
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function validateDatevSettings(
  values: Readonly<Record<string, string | undefined>>,
): readonly DatevSettingError[] {
  const errors: DatevSettingError[] = [];
  for (const field of DATEV_SETTING_FIELDS) {
    const trimmed = (values[field.key] ?? '').trim();
    if (trimmed.length === 0) {
      if (field.required) {
        errors.push({
          key: field.key,
          reason: 'missing',
          message: `${field.label} is required.`,
        });
      }
      continue;
    }
    if (field.kind === 'positive-integer') {
      if (!isPositiveIntegerString(trimmed)) {
        errors.push({
          key: field.key,
          reason: 'invalid',
          message: `${field.label} must be a positive whole number.`,
        });
        continue;
      }
      const n = Number(trimmed);
      if (
        (field.min !== undefined && n < field.min) ||
        (field.max !== undefined && n > field.max)
      ) {
        errors.push({
          key: field.key,
          reason: 'invalid',
          message: `${field.label} must be between ${field.min} and ${field.max}.`,
        });
      }
    } else if (field.kind === 'iso-date') {
      if (!isRealIsoDate(trimmed)) {
        errors.push({
          key: field.key,
          reason: 'invalid',
          message: `${field.label} must be a real date in YYYY-MM-DD format.`,
        });
      }
    }
  }
  return errors;
}

// The DATEV keys whose value is now blank and should be deleted from
// tenant_settings on save. buildDatevSettingsUpsert omits blank fields from
// the upsert, but omitting a row does not remove a previously-saved value —
// so a cleared optional (e.g. generator_info) would otherwise persist and the
// builder would keep honouring the stale value instead of its default. Pair
// this with buildDatevSettingsUpsert: upsert the non-blank rows, delete these.
export function datevSettingKeysToClear(
  values: Readonly<Record<string, string | undefined>>,
): string[] {
  return DATEV_SETTING_FIELDS.filter((field) => (values[field.key] ?? '').trim().length === 0).map(
    (field) => field.key,
  );
}
